import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { format, parseISO, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, addMonths, subMonths, isSameMonth, isSameDay, isBefore, startOfDay } from "date-fns";
import { toast } from "sonner";
import { api, adminApi, getAdminPasscode, setAdminPasscode, clearAdminPasscode, COACHES, SESSION_TYPES, LOCATIONS } from "@/lib/api";

import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { LogOut, MoreHorizontal, RefreshCw, ShieldCheck, Trash2, CheckCircle2, XCircle, Hourglass, Calendar as CalIcon, Mail, Phone, Eye, MapPin, Clock, User2, Users2, Footprints, Building2, Target, MessageSquare, Package, ChevronLeft, ChevronRight, CalendarDays, Search, PoundSterling, Plus, PenLine, Save } from "lucide-react";

const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  confirmed: "bg-emerald-100 text-emerald-800 border-emerald-200",
  completed: "bg-blue-100 text-blue-800 border-blue-200",
  cancelled: "bg-rose-100 text-rose-800 border-rose-200",
};

const PAYMENT_STYLES = {
  unpaid: "bg-amber-50 text-amber-700 border-amber-200",
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  refunded: "bg-slate-100 text-slate-600 border-slate-300",
};

const PACKAGE_LABELS = {
  single: "Single session",
  block_3: "3 sessions (£50)",
  block_5: "Block of 5",
  block_10: "Block of 10",
  pair: "2-player session",
};

// Estimated revenue per booking (used in monthly revenue stat)
function packageRevenue(b) {
  const p = b.package || "single";
  if (p === "pair") return 30;
  if (p === "block_3") return 50;
  if (p === "block_5") return b.coach === "james" ? 225 : 90;
  if (p === "block_10") return b.coach === "james" ? 425 : 170;
  // single
  return b.coach === "james" ? 50 : 20;
}

export default function Admin() {
  const [authed, setAuthed] = useState(Boolean(getAdminPasscode()));

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      {!authed ? (
        <LoginCard onSuccess={() => setAuthed(true)} />
      ) : (
        <Dashboard onLogout={() => { clearAdminPasscode(); setAuthed(false); }} />
      )}
      <Footer />
    </div>
  );
}

function LoginCard({ onSuccess }) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!code) return;
    setLoading(true);
    try {
      await api.post("/admin/login", { passcode: code });
      setAdminPasscode(code);
      toast.success("Welcome back");
      onSuccess();
    } catch (err) {
      toast.error("Invalid passcode");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-20 md:py-28">
      <div className="max-w-md mx-auto px-5">
        <Card className="rounded-3xl border-slate-200 p-8 bg-white">
          <div className="w-12 h-12 rounded-2xl bg-blue-50 text-blue-700 grid place-items-center border border-blue-100 mb-4">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <h1 className="font-display text-3xl font-black tracking-tighter text-slate-950">
            Coach login
          </h1>
          <p className="text-slate-600 mt-2 text-sm">Enter the admin passcode to view bookings.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="passcode" className="text-sm font-semibold text-slate-800">Passcode</Label>
              <Input
                id="passcode"
                data-testid="admin-passcode-input"
                type="password"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="rounded-xl mt-2 h-11"
                placeholder="••••••••"
                autoFocus
              />
            </div>
            <Button
              type="submit"
              data-testid="admin-login-btn"
              disabled={loading}
              className="w-full rounded-full bg-slate-950 hover:bg-slate-800 text-white py-6 font-bold"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>
          <Link to="/" className="block text-center text-sm text-slate-500 hover:text-slate-700 mt-6">
            Back to site
          </Link>
        </Card>
      </div>
    </section>
  );
}

function Dashboard({ onLogout }) {
  const [rawBookings, setRawBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [coachFilter, setCoachFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [hideCompleted, setHideCompleted] = useState(true);
  const [tab, setTab] = useState("list");
  const [openBooking, setOpenBooking] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (coachFilter !== "all") params.coach = coachFilter;
      if (statusFilter !== "all") params.status = statusFilter;
      const res = await adminApi.get("/bookings", { params });
      setRawBookings(res.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired — please sign in again");
        clearAdminPasscode();
        onLogout();
      } else {
        toast.error("Failed to load bookings");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [coachFilter, statusFilter]);

  const setStatus = async (id, status) => {
    try {
      await adminApi.patch(`/bookings/${id}`, { status });
      toast.success(`Marked as ${status}`);
      load();
    } catch {
      toast.error("Couldn't update status");
    }
  };

  const savePatch = async (id, patch) => {
    try {
      const res = await adminApi.patch(`/bookings/${id}`, patch);
      toast.success("Saved");
      load();
      return res.data;
    } catch {
      toast.error("Couldn't save changes");
      return null;
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this booking? This can't be undone.")) return;
    try {
      await adminApi.delete(`/bookings/${id}`);
      toast.success("Booking deleted");
      load();
    } catch {
      toast.error("Couldn't delete booking");
    }
  };

  // Filtered bookings (hide completed/cancelled by default, search, payment filter)
  const bookings = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rawBookings.filter((b) => {
      if (hideCompleted && (b.status === "completed" || b.status === "cancelled")) return false;
      if (paymentFilter !== "all" && (b.payment_status || "unpaid") !== paymentFilter) return false;
      if (q) {
        const hay = `${b.player_name} ${b.parent_name} ${b.parent_email} ${b.parent_phone} ${b.second_player_name || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rawBookings, hideCompleted, paymentFilter, search]);

  const counts = useMemo(() => {
    const c = { total: rawBookings.length, pending: 0, confirmed: 0, completed: 0, cancelled: 0, unpaid: 0, revenue_month: 0 };
    const now = new Date();
    const monthKey = format(now, "yyyy-MM");
    rawBookings.forEach((b) => {
      c[b.status] = (c[b.status] || 0) + 1;
      if ((b.payment_status || "unpaid") === "unpaid" && b.status !== "cancelled") c.unpaid += 1;
      if ((b.payment_status || "") === "paid" && (b.preferred_date || "").startsWith(monthKey)) {
        c.revenue_month += packageRevenue(b);
      }
    });
    return c;
  }, [rawBookings]);

  // Calendar bookings grouped by date
  const bookingsByDate = useMemo(() => {
    const map = new Map();
    bookings.forEach((b) => {
      const d = b.preferred_date;
      if (!map.has(d)) map.set(d, []);
      map.get(d).push(b);
    });
    return map;
  }, [bookings]);

  const [calDate, setCalDate] = useState(new Date());
  const [monthCursor, setMonthCursor] = useState(new Date());
  const calDateKey = format(calDate, "yyyy-MM-dd");
  const dayBookings = bookingsByDate.get(calDateKey) || [];

  const bookedDays = useMemo(() => {
    return Array.from(bookingsByDate.keys()).map((k) => parseISO(k));
  }, [bookingsByDate]);

  // Month grid cells
  const monthCells = useMemo(() => {
    const start = startOfWeek(startOfMonth(monthCursor), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(monthCursor), { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [monthCursor]);

  // Upcoming sessions (today onwards, sorted)
  const upcomingSessions = useMemo(() => {
    const today = startOfDay(new Date());
    return [...bookings]
      .filter((b) => {
        try {
          const d = parseISO(b.preferred_date);
          return !isBefore(d, today);
        } catch { return false; }
      })
      .sort((a, b) => {
        const c = a.preferred_date.localeCompare(b.preferred_date);
        if (c !== 0) return c;
        return a.preferred_time.localeCompare(b.preferred_time);
      });
  }, [bookings]);

  return (
    <section className="py-10 md:py-14">
      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] font-bold text-blue-600 mb-2">
              Coach dashboard
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-slate-950">
              Bookings
            </h1>
            <p className="text-slate-600 mt-2">Track, confirm and manage session requests.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setShowAddDialog(true)}
              data-testid="admin-add-booking-btn"
              className="rounded-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Plus className="w-4 h-4 mr-2" /> Add booking
            </Button>
            <Button
              variant="outline"
              onClick={load}
              data-testid="admin-refresh-btn"
              className="rounded-full"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <Button
              variant="outline"
              onClick={onLogout}
              data-testid="admin-logout-btn"
              className="rounded-full"
            >
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 md:gap-4 mb-8">
          <StatCard label="Total" value={counts.total} icon={CalIcon} testid="stat-total" />
          <StatCard label="Pending" value={counts.pending} icon={Hourglass} tone="amber" testid="stat-pending" />
          <StatCard label="Confirmed" value={counts.confirmed} icon={CheckCircle2} tone="emerald" testid="stat-confirmed" />
          <StatCard label="Completed" value={counts.completed} icon={CheckCircle2} tone="blue" testid="stat-completed" />
          <StatCard label="Unpaid" value={counts.unpaid} icon={PoundSterling} tone="amber" testid="stat-unpaid" />
          <StatCard label="Paid this month" value={`£${counts.revenue_month.toFixed(0)}`} icon={PoundSterling} tone="emerald" testid="stat-revenue" />
        </div>

        {/* Filters */}
        <Card className="rounded-3xl border-slate-200 p-5 md:p-6 mb-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
            <div className="lg:col-span-2">
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Search</Label>
              <div className="relative mt-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  data-testid="filter-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Player, parent, email or phone…"
                  className="rounded-xl h-11 pl-9"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Coach</Label>
              <Select value={coachFilter} onValueChange={setCoachFilter}>
                <SelectTrigger data-testid="filter-coach" className="rounded-xl h-11 mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All coaches</SelectItem>
                  <SelectItem value="jacob">Jacob</SelectItem>
                  <SelectItem value="james">James</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Payment</Label>
              <Select value={paymentFilter} onValueChange={setPaymentFilter}>
                <SelectTrigger data-testid="filter-payment" className="rounded-xl h-11 mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All payments</SelectItem>
                  <SelectItem value="unpaid">Unpaid</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                  <SelectItem value="refunded">Refunded</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-5 pt-5 border-t border-slate-200 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Switch
                id="hide-completed"
                data-testid="switch-hide-completed"
                checked={hideCompleted}
                onCheckedChange={setHideCompleted}
              />
              <Label htmlFor="hide-completed" className="text-sm font-semibold text-slate-800 cursor-pointer">
                Hide completed & cancelled
              </Label>
            </div>
            <div className="text-sm text-slate-500">
              Showing <span className="font-bold text-slate-900">{bookings.length}</span> of {rawBookings.length}
            </div>
          </div>
        </Card>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="admin-tabs" className="bg-slate-200/60 p-1 rounded-full">
            <TabsTrigger data-testid="tab-list" value="list" className="rounded-full px-5">List</TabsTrigger>
            <TabsTrigger data-testid="tab-calendar" value="calendar" className="rounded-full px-5">Calendar</TabsTrigger>
            <TabsTrigger data-testid="tab-upcoming" value="upcoming" className="rounded-full px-5">Upcoming</TabsTrigger>
          </TabsList>

          <TabsContent value="list" className="mt-6">
            <Card className="rounded-3xl border-slate-200 overflow-hidden">
              {loading ? (
                <div className="p-10 text-center text-slate-500">Loading bookings…</div>
              ) : bookings.length === 0 ? (
                <div className="p-10 text-center text-slate-500">No bookings yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Player</TableHead>
                        <TableHead>Coach</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>When</TableHead>
                        <TableHead>Where</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Payment</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {bookings.map((b) => (
                        <TableRow key={b.id} data-testid={`booking-row-${b.id}`} onClick={() => setOpenBooking(b)} className="cursor-pointer">
                          <TableCell>
                            <div className="font-semibold text-slate-900 flex items-center gap-2">
                              {b.player_name}
                              {b.custom_request && (
                                <span title="Custom request" className="inline-flex items-center text-blue-600">
                                  <MessageSquare className="w-3.5 h-3.5" />
                                </span>
                              )}
                              {b.coach_notes && (
                                <span title="Coach notes" className="inline-flex items-center text-slate-500">
                                  <PenLine className="w-3.5 h-3.5" />
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-slate-500">Age {b.player_age}{b.player_position ? ` · ${b.player_position}` : ""}</div>
                          </TableCell>
                          <TableCell className="capitalize font-medium">{COACHES[b.coach]?.name}</TableCell>
                          <TableCell className="text-sm">
                            <div>{SESSION_TYPES.find(s=>s.id===b.session_type)?.label}</div>
                            <div className="text-[11px] text-slate-500">{PACKAGE_LABELS[b.package] || b.package}</div>
                          </TableCell>
                          <TableCell className="text-sm">
                            <div className="font-semibold text-slate-900">{b.preferred_date}</div>
                            <div className="text-xs text-slate-500">{b.preferred_time}</div>
                          </TableCell>
                          <TableCell className="text-sm">{LOCATIONS.find(l=>l.id===b.location)?.label}</TableCell>
                          <TableCell className="text-xs">
                            <div className="font-medium text-slate-700">{b.parent_name}</div>
                            <div className="text-slate-500">{b.parent_email}</div>
                            <div className="text-slate-500">{b.parent_phone}</div>
                          </TableCell>
                          <TableCell>
                            <Badge className={`${STATUS_STYLES[b.status]} border font-bold uppercase text-[10px] tracking-wider`}>
                              {b.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={`${PAYMENT_STYLES[b.payment_status || "unpaid"]} border font-bold uppercase text-[10px] tracking-wider`}>
                              {b.payment_status || "unpaid"}
                            </Badge>
                          </TableCell>
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" data-testid={`row-actions-${b.id}`}>
                                  <MoreHorizontal className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => setOpenBooking(b)} data-testid={`view-${b.id}`}>
                                  <Eye className="w-4 h-4 mr-2 text-slate-700" /> View / edit details
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuLabel>Status</DropdownMenuLabel>
                                <DropdownMenuItem onClick={() => setStatus(b.id, "confirmed")} data-testid={`set-confirmed-${b.id}`}>
                                  <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-600" /> Confirm
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setStatus(b.id, "completed")} data-testid={`set-completed-${b.id}`}>
                                  <CheckCircle2 className="w-4 h-4 mr-2 text-blue-600" /> Mark completed
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setStatus(b.id, "pending")} data-testid={`set-pending-${b.id}`}>
                                  <Hourglass className="w-4 h-4 mr-2 text-amber-600" /> Mark pending
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => setStatus(b.id, "cancelled")} data-testid={`set-cancelled-${b.id}`}>
                                  <XCircle className="w-4 h-4 mr-2 text-rose-600" /> Cancel
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuLabel>Payment</DropdownMenuLabel>
                                <DropdownMenuItem onClick={() => savePatch(b.id, { payment_status: "paid" })} data-testid={`pay-paid-${b.id}`}>
                                  <PoundSterling className="w-4 h-4 mr-2 text-emerald-600" /> Mark paid
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => savePatch(b.id, { payment_status: "unpaid" })} data-testid={`pay-unpaid-${b.id}`}>
                                  <PoundSterling className="w-4 h-4 mr-2 text-amber-600" /> Mark unpaid
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => savePatch(b.id, { payment_status: "refunded" })} data-testid={`pay-refunded-${b.id}`}>
                                  <PoundSterling className="w-4 h-4 mr-2 text-slate-500" /> Mark refunded
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => remove(b.id)} className="text-rose-600" data-testid={`delete-${b.id}`}>
                                  <Trash2 className="w-4 h-4 mr-2" /> Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="calendar" className="mt-6">
            <Card className="rounded-3xl border-slate-200 overflow-hidden">
              {/* Month header */}
              <div className="flex flex-wrap items-center justify-between gap-3 px-5 md:px-7 pt-5 pb-4 border-b border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-700 grid place-items-center border border-blue-100">
                    <CalendarDays className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="font-display text-2xl font-black tracking-tight text-slate-950">
                      {format(monthCursor, "MMMM yyyy")}
                    </div>
                    <div className="text-xs text-slate-500 font-medium">
                      {bookings.length} total · click any day for details
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    data-testid="cal-prev-month"
                    onClick={() => setMonthCursor((d) => subMonths(d, 1))}
                    className="rounded-full"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    data-testid="cal-today-btn"
                    onClick={() => { const n = new Date(); setMonthCursor(n); setCalDate(n); }}
                    className="rounded-full text-sm"
                  >
                    Today
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    data-testid="cal-next-month"
                    onClick={() => setMonthCursor((d) => addMonths(d, 1))}
                    className="rounded-full"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Weekday header */}
              <div className="grid grid-cols-7 text-[11px] uppercase tracking-wider font-bold text-slate-500 border-b border-slate-200 bg-slate-50">
                {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
                  <div key={d} className="px-3 py-2 text-center">{d}</div>
                ))}
              </div>

              {/* Month grid */}
              <div className="grid grid-cols-7" data-testid="month-grid">
                {monthCells.map((day) => {
                  const key = format(day, "yyyy-MM-dd");
                  const dayBs = (bookingsByDate.get(key) || []).sort((a, b) => a.preferred_time.localeCompare(b.preferred_time));
                  const inMonth = isSameMonth(day, monthCursor);
                  const isToday = isSameDay(day, new Date());
                  const isSelected = isSameDay(day, calDate);
                  return (
                    <button
                      type="button"
                      key={key}
                      data-testid={`day-cell-${key}`}
                      onClick={() => setCalDate(day)}
                      className={`relative text-left min-h-[110px] md:min-h-[120px] p-2 md:p-2.5 border-b border-r border-slate-200 transition ${inMonth ? "bg-white hover:bg-slate-50" : "bg-slate-50/60 text-slate-400"} ${isSelected ? "ring-2 ring-blue-500 ring-inset z-10" : ""}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className={`text-xs font-bold ${isToday ? "bg-blue-600 text-white rounded-full w-6 h-6 grid place-items-center" : inMonth ? "text-slate-700" : "text-slate-400"}`}>
                          {format(day, "d")}
                        </span>
                        {dayBs.length > 2 && (
                          <span className="text-[10px] font-bold text-slate-500">+{dayBs.length}</span>
                        )}
                      </div>
                      <div className="mt-1.5 space-y-1">
                        {dayBs.slice(0, 3).map((b) => {
                          const coachColor = b.coach === "james"
                            ? "bg-blue-100 text-blue-800 border-blue-200"
                            : "bg-emerald-100 text-emerald-800 border-emerald-200";
                          const cancelled = b.status === "cancelled" ? "opacity-50 line-through" : "";
                          return (
                            <div
                              key={b.id}
                              onClick={(e) => { e.stopPropagation(); setOpenBooking(b); }}
                              data-testid={`day-pill-${b.id}`}
                              className={`text-[10.5px] md:text-[11px] font-semibold px-2 py-1 rounded-md border ${coachColor} ${cancelled} truncate hover:brightness-95`}
                              title={`${b.preferred_time} · ${b.player_name} (${COACHES[b.coach]?.name})`}
                            >
                              <span className="font-bold">{b.preferred_time}</span> {b.player_name}
                            </div>
                          );
                        })}
                        {dayBs.length > 3 && (
                          <div className="text-[10px] text-slate-500 font-semibold pl-1">
                            +{dayBs.length - 3} more
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="flex flex-wrap items-center gap-4 px-5 md:px-7 py-4 border-t border-slate-200 bg-slate-50 text-xs text-slate-600">
                <div className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-emerald-100 border border-emerald-300 inline-block" /> Jacob</div>
                <div className="flex items-center gap-2"><span className="w-3 h-3 rounded bg-blue-100 border border-blue-300 inline-block" /> James</div>
                <div className="flex items-center gap-2"><span className="w-3 h-3 rounded-full bg-blue-600 inline-block" /> Today</div>
                <div className="flex items-center gap-2"><span className="line-through text-slate-400">Strikethrough</span> = cancelled</div>
              </div>
            </Card>

            {/* Selected day detail panel */}
            <Card className="rounded-3xl border-slate-200 p-6 mt-6">
              <div className="flex items-center justify-between gap-3 mb-4">
                <div>
                  <div className="font-display text-xl font-extrabold text-slate-950">
                    {format(calDate, "EEEE d MMMM yyyy")}
                  </div>
                  <div className="text-sm text-slate-500 mt-1">{dayBookings.length} session{dayBookings.length === 1 ? "" : "s"}</div>
                </div>
              </div>
              <div className="space-y-3">
                {dayBookings.length === 0 && (
                  <div className="text-sm text-slate-500 italic">Nothing scheduled for this day.</div>
                )}
                {dayBookings
                  .sort((a, b) => a.preferred_time.localeCompare(b.preferred_time))
                  .map((b) => (
                    <button
                      type="button"
                      key={b.id}
                      data-testid={`cal-booking-${b.id}`}
                      onClick={() => setOpenBooking(b)}
                      className="w-full text-left rounded-2xl border border-slate-200 p-4 bg-white hover:border-blue-300 hover:bg-blue-50/30 transition"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className={`shrink-0 w-1.5 h-12 rounded-full ${b.coach === "james" ? "bg-blue-600" : "bg-emerald-500"}`} />
                          <div className="min-w-0">
                            <div className="font-bold text-slate-950 truncate">{b.preferred_time} · {b.player_name}</div>
                            <div className="text-xs text-slate-500 truncate">{COACHES[b.coach]?.name} · {SESSION_TYPES.find(s=>s.id===b.session_type)?.label}</div>
                          </div>
                        </div>
                        <Badge className={`${STATUS_STYLES[b.status]} border font-bold uppercase text-[10px] tracking-wider shrink-0`}>
                          {b.status}
                        </Badge>
                      </div>
                    </button>
                  ))}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="upcoming" className="mt-6">
            <Card className="rounded-3xl border-slate-200 p-6">
              <div className="flex items-center justify-between gap-3 mb-5">
                <div>
                  <div className="font-display text-2xl font-extrabold text-slate-950">Upcoming sessions</div>
                  <div className="text-sm text-slate-500">All confirmed and pending sessions from today onwards.</div>
                </div>
                <div className="text-sm text-slate-500 font-semibold">{upcomingSessions.length} total</div>
              </div>

              {upcomingSessions.length === 0 ? (
                <div className="text-sm text-slate-500 italic py-6 text-center">No upcoming sessions.</div>
              ) : (
                <div className="space-y-3" data-testid="upcoming-list">
                  {upcomingSessions.map((b) => (
                    <button
                      type="button"
                      key={b.id}
                      data-testid={`upcoming-${b.id}`}
                      onClick={() => setOpenBooking(b)}
                      className="w-full text-left rounded-2xl border border-slate-200 p-4 bg-white hover:border-blue-300 hover:bg-blue-50/30 transition flex items-center gap-4"
                    >
                      <div className={`shrink-0 w-14 h-14 rounded-2xl grid place-items-center text-center font-display ${b.coach === "james" ? "bg-blue-50 text-blue-700 border border-blue-200" : "bg-emerald-50 text-emerald-700 border border-emerald-200"}`}>
                        <div className="text-[10px] font-bold uppercase leading-none">{format(parseISO(b.preferred_date), "MMM")}</div>
                        <div className="text-2xl font-black leading-none mt-0.5">{format(parseISO(b.preferred_date), "d")}</div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-bold text-slate-950 truncate">{b.preferred_time} · {b.player_name}</div>
                        <div className="text-xs text-slate-500 truncate">{COACHES[b.coach]?.name} · {SESSION_TYPES.find(s=>s.id===b.session_type)?.label} · {LOCATIONS.find(l=>l.id===b.location)?.label}</div>
                      </div>
                      <Badge className={`${STATUS_STYLES[b.status]} border font-bold uppercase text-[10px] tracking-wider shrink-0`}>
                        {b.status}
                      </Badge>
                    </button>
                  ))}
                </div>
              )}
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <BookingDetailDialog
        booking={openBooking}
        onClose={() => setOpenBooking(null)}
        onStatusChange={(status) => {
          if (openBooking) setStatus(openBooking.id, status);
          setOpenBooking(null);
        }}
        onSave={async (patch) => {
          if (openBooking) {
            const updated = await savePatch(openBooking.id, patch);
            if (updated) setOpenBooking(updated);
          }
        }}
      />

      <AddBookingDialog
        open={showAddDialog}
        onClose={() => setShowAddDialog(false)}
        onCreated={() => { setShowAddDialog(false); load(); }}
      />
    </section>
  );
}

function BookingDetailDialog({ booking, onClose, onStatusChange, onSave }) {
  const open = Boolean(booking);
  const [coachNotes, setCoachNotes] = useState("");
  const [payment, setPayment] = useState("unpaid");
  const [savingNotes, setSavingNotes] = useState(false);

  useEffect(() => {
    if (booking) {
      setCoachNotes(booking.coach_notes || "");
      setPayment(booking.payment_status || "unpaid");
    }
  }, [booking?.id, booking?.coach_notes, booking?.payment_status]);

  if (!booking) {
    return (
      <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
        <DialogContent className="max-w-2xl" />
      </Dialog>
    );
  }
  const sessionType = SESSION_TYPES.find((s) => s.id === booking.session_type)?.label;
  const location = LOCATIONS.find((l) => l.id === booking.location)?.label;
  const pkg =
    booking.package === "single" ? "Single session"
    : booking.package === "block_3" ? "3 sessions (£50)"
    : booking.package === "block_5" ? "Block of 5"
    : booking.package === "block_10" ? "Block of 10"
    : booking.package === "pair" ? "2-player session (£30 split)"
    : booking.package;
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="booking-detail-dialog" className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <DialogTitle className="font-display text-2xl font-black tracking-tight">
                {booking.player_name}
              </DialogTitle>
              <DialogDescription className="mt-1">
                Age {booking.player_age}
                {booking.player_position ? ` · ${booking.player_position}` : ""}
                {booking.preferred_foot ? ` · ${booking.preferred_foot} footed` : ""}
              </DialogDescription>
            </div>
            <div className="flex flex-col items-end gap-1">
              <Badge className={`${STATUS_STYLES[booking.status]} border font-bold uppercase text-[10px] tracking-wider`}>
                {booking.status}
              </Badge>
              <Badge className={`${PAYMENT_STYLES[booking.payment_status || "unpaid"]} border font-bold uppercase text-[10px] tracking-wider`}>
                {booking.payment_status || "unpaid"}
              </Badge>
            </div>
          </div>
        </DialogHeader>

        <div className="grid sm:grid-cols-2 gap-3 mt-2">
          <DetailRow icon={User2} label="Coach" value={COACHES[booking.coach]?.name} />
          <DetailRow icon={Target} label="Session type" value={sessionType} />
          <DetailRow icon={Package} label="Package" value={pkg} />
          <DetailRow icon={CalIcon} label="Date" value={booking.preferred_date} />
          <DetailRow icon={Clock} label="Time" value={booking.preferred_time} />
          <DetailRow icon={MapPin} label="Location" value={location} className="sm:col-span-2" />
          {booking.home_address && (
            <DetailRow icon={MapPin} label="Address" value={booking.home_address} className="sm:col-span-2" />
          )}
          {booking.current_club && (
            <DetailRow icon={Building2} label="Current club" value={booking.current_club} />
          )}
          {booking.current_level && (
            <DetailRow icon={Target} label="Current level" value={booking.current_level} />
          )}
          {booking.preferred_foot && (
            <DetailRow icon={Footprints} label="Preferred foot" value={booking.preferred_foot} />
          )}
          {booking.second_player_name && (
            <DetailRow
              icon={Users2}
              label="Second player"
              value={`${booking.second_player_name}${booking.second_player_age ? ` · Age ${booking.second_player_age}` : ""}`}
              className="sm:col-span-2"
            />
          )}
        </div>

        {booking.focus_areas && booking.focus_areas.length > 0 && (
          <div className="mt-5">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">Focus areas</div>
            <div className="flex flex-wrap gap-2">
              {booking.focus_areas.map((f) => (
                <span key={f} className="text-xs font-bold bg-blue-50 text-blue-700 border border-blue-200 px-3 py-1 rounded-full">
                  {f}
                </span>
              ))}
            </div>
          </div>
        )}

        {booking.custom_request && (
          <div className="mt-5 rounded-2xl border border-blue-200 bg-blue-50/50 p-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-blue-700 font-bold mb-2">
              <MessageSquare className="w-3.5 h-3.5" />
              Custom request / tweaks
            </div>
            <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed">{booking.custom_request}</p>
          </div>
        )}

        {booking.equipment_needed && (
          <div className="mt-3 text-sm">
            <span className="font-bold text-slate-900">Equipment: </span>
            <span className="text-slate-700">{booking.equipment_needed}</span>
          </div>
        )}

        {booking.notes && (
          <div className="mt-5">
            <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-2">Other notes</div>
            <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{booking.notes}</p>
          </div>
        )}

        <div className="mt-6 pt-5 border-t border-slate-200">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-bold mb-3">Contact</div>
          <div className="grid sm:grid-cols-2 gap-2 text-sm">
            <div className="flex items-center gap-2 text-slate-700"><User2 className="w-4 h-4 text-slate-500" /> {booking.parent_name}</div>
            <div className="flex items-center gap-2 text-slate-700"><Mail className="w-4 h-4 text-slate-500" /> <a href={`mailto:${booking.parent_email}`} className="hover:underline">{booking.parent_email}</a></div>
            <div className="flex items-center gap-2 text-slate-700"><Phone className="w-4 h-4 text-slate-500" /> <a href={`tel:${booking.parent_phone}`} className="hover:underline">{booking.parent_phone}</a></div>
          </div>
        </div>

        {/* Coach notes editor */}
        <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50/40 p-4">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-slate-600 font-bold mb-2">
            <PenLine className="w-3.5 h-3.5" />
            Coach notes (private)
          </div>
          <Textarea
            data-testid="dialog-coach-notes"
            value={coachNotes}
            onChange={(e) => setCoachNotes(e.target.value)}
            rows={3}
            placeholder="Session summary, what to work on next time, wins, injuries…"
            className="rounded-xl bg-white"
          />
        </div>

        {/* Payment + save */}
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Payment</Label>
            <Select value={payment} onValueChange={setPayment}>
              <SelectTrigger data-testid="dialog-payment-select" className="rounded-full h-9 w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unpaid">Unpaid</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
                <SelectItem value="refunded">Refunded</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            data-testid="dialog-save-btn"
            disabled={savingNotes}
            onClick={async () => {
              setSavingNotes(true);
              await onSave({ coach_notes: coachNotes, payment_status: payment });
              setSavingNotes(false);
            }}
            className="rounded-full bg-slate-950 hover:bg-slate-800 text-white"
          >
            <Save className="w-4 h-4 mr-2" /> {savingNotes ? "Saving…" : "Save changes"}
          </Button>
        </div>

        <div className="mt-6 pt-5 border-t border-slate-200 flex flex-wrap gap-2 justify-end">
          {booking.status !== "confirmed" && (
            <Button data-testid="dialog-confirm-btn" onClick={() => onStatusChange("confirmed")} className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white">
              <CheckCircle2 className="w-4 h-4 mr-2" /> Confirm
            </Button>
          )}
          {booking.status !== "completed" && (
            <Button data-testid="dialog-complete-btn" variant="outline" onClick={() => onStatusChange("completed")} className="rounded-full">
              Mark completed
            </Button>
          )}
          {booking.status !== "cancelled" && (
            <Button data-testid="dialog-cancel-btn" variant="outline" onClick={() => onStatusChange("cancelled")} className="rounded-full text-rose-600 border-rose-200 hover:bg-rose-50">
              Cancel session
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AddBookingDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    player_name: "",
    player_age: "14",
    parent_name: "Coach entry",
    parent_email: "coach@grantfootball.local",
    parent_phone: "-",
    coach: "jacob",
    session_type: "skill_specific",
    location: "southampton_sports_centre",
    package: "single",
    preferred_date: format(new Date(), "yyyy-MM-dd"),
    preferred_time: "17:00",
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.player_name.trim() || !form.preferred_date || !form.preferred_time) {
      toast.error("Player name, date and time are required");
      return;
    }
    setSaving(true);
    try {
      await adminApi.post("/admin/bookings", {
        ...form,
        player_age: Number(form.player_age) || 14,
      });
      toast.success("Booking added");
      onCreated();
      setForm((f) => ({ ...f, player_name: "" }));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't add booking");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="add-booking-dialog" className="max-w-lg rounded-3xl">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black tracking-tight">Add booking</DialogTitle>
          <DialogDescription>Quick entry for a session you&apos;ve arranged outside the site.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 mt-2">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Player name</Label>
            <Input data-testid="add-player-name" value={form.player_name} onChange={(e)=>upd("player_name", e.target.value)} className="rounded-xl h-11 mt-1" placeholder="e.g. Harry Smith" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Age</Label>
              <Input data-testid="add-player-age" type="number" min={8} max={25} value={form.player_age} onChange={(e)=>upd("player_age", e.target.value)} className="rounded-xl h-11 mt-1" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Coach</Label>
              <Select value={form.coach} onValueChange={(v) => upd("coach", v)}>
                <SelectTrigger data-testid="add-coach" className="rounded-xl h-11 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="jacob">Jacob</SelectItem>
                  <SelectItem value="james">James</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Session type</Label>
              <Select value={form.session_type} onValueChange={(v) => upd("session_type", v)}>
                <SelectTrigger data-testid="add-session-type" className="rounded-xl h-11 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SESSION_TYPES.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Package</Label>
              <Select value={form.package} onValueChange={(v) => upd("package", v)}>
                <SelectTrigger data-testid="add-package" className="rounded-xl h-11 mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="single">Single</SelectItem>
                  {form.coach === "jacob" && <SelectItem value="block_3">3 for £50</SelectItem>}
                  <SelectItem value="block_5">Block of 5</SelectItem>
                  <SelectItem value="block_10">Block of 10</SelectItem>
                  {form.coach === "jacob" && <SelectItem value="pair">2-player (£30)</SelectItem>}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Location</Label>
            <Select value={form.location} onValueChange={(v) => upd("location", v)}>
              <SelectTrigger data-testid="add-location" className="rounded-xl h-11 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LOCATIONS.map((l) => (
                  <SelectItem key={l.id} value={l.id}>{l.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Date</Label>
              <Input data-testid="add-date" type="date" value={form.preferred_date} onChange={(e)=>upd("preferred_date", e.target.value)} className="rounded-xl h-11 mt-1" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Time</Label>
              <Input data-testid="add-time" type="time" value={form.preferred_time} onChange={(e)=>upd("preferred_time", e.target.value)} className="rounded-xl h-11 mt-1" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Parent name</Label>
              <Input data-testid="add-parent-name" value={form.parent_name} onChange={(e)=>upd("parent_name", e.target.value)} className="rounded-xl h-11 mt-1" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Phone</Label>
              <Input data-testid="add-parent-phone" value={form.parent_phone} onChange={(e)=>upd("parent_phone", e.target.value)} className="rounded-xl h-11 mt-1" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Email</Label>
            <Input data-testid="add-parent-email" type="email" value={form.parent_email} onChange={(e)=>upd("parent_email", e.target.value)} className="rounded-xl h-11 mt-1" />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="add-submit" className="rounded-full bg-blue-600 hover:bg-blue-700 text-white">
              {saving ? "Saving…" : "Add booking"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DetailRow({ icon: Icon, label, value, className = "" }) {
  return (
    <div className={`flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50/60 p-3 ${className}`}>
      <Icon className="w-4 h-4 mt-0.5 text-slate-500 shrink-0" />
      <div>
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">{label}</div>
        <div className="text-sm font-semibold text-slate-900">{value || "—"}</div>
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, tone = "slate", testid }) {
  const tones = {
    slate: "bg-slate-100 text-slate-700 border-slate-200",
    amber: "bg-amber-100 text-amber-800 border-amber-200",
    emerald: "bg-emerald-100 text-emerald-800 border-emerald-200",
    blue: "bg-blue-100 text-blue-800 border-blue-200",
    rose: "bg-rose-100 text-rose-800 border-rose-200",
  };
  return (
    <Card data-testid={testid} className="rounded-2xl border-slate-200 p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-xl grid place-items-center border ${tones[tone]}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
        <div className="font-display text-2xl font-black text-slate-950 leading-none mt-1">{value}</div>
      </div>
    </Card>
  );
}
